import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  makeStyles,
  OverlayDrawer,
  SelectTabData,
  SelectTabEvent,
  Spinner,
  Tab,
  TabList,
  Tooltip,
  useRestoreFocusSource,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  DocumentOnePageSparkleRegular,
  TextBulletListSquareSparkleRegular,
  WindowNew24Regular,
} from '@fluentui/react-icons';
import React, { useMemo, useRef } from 'react';
import {
  useGetRecordingQuery,
  useRenameMeetingMutation,
} from '../../queries/recording';
import { useIsLargeScreen } from '../../utils/use-is-large-screen';
import { openRecordingInTeamsWindow } from '../../utils/open-in-teams-window';
import { GlobalInformations } from '../recording-global-informations';
import { MeetingNotes } from '../meeting-notes';
import { MeetingTranscript } from '../meeting-transcript';
import { useTeamsContext } from '../../providers/teams-context-provider';
import { canEditRecording } from '../../utils/can-edit-recording';

const useStyle = makeStyles({
  wrapperStyle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '20px 0',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
});

export const RecordingDrawer = (props: {
  recordingId?: string;
  onClose: () => void;
}) => {
  const { recordingId, onClose } = props;
  const { data, isLoading } = useGetRecordingQuery(recordingId);
  const { mutate } = useRenameMeetingMutation();
  const { teamsContext } = useTeamsContext();

  const restoreFocusSourceAttributes = useRestoreFocusSource();

  const isLargeScreen = useIsLargeScreen();

  const globalInfosRef = useRef<HTMLDivElement>(null);

  const editable = useMemo(() => {
    if (!data) return false;
    return canEditRecording(
      teamsContext?.user?.id,
      data.submittedBy,
      data.roster
    );
  }, [teamsContext?.user?.id, data]);

  const [selectedTab, setSelectedTab] = React.useState<string>('meeting-notes');

  const onTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as string);
  };

  const handleOpenNewWindow = () => {
    if (recordingId) {
      openRecordingInTeamsWindow(recordingId);
    }
  };

  const styles = useStyle();

  return (
    <OverlayDrawer
      {...restoreFocusSourceAttributes}
      size={isLargeScreen ? 'large' : 'full'}
      position="end"
      open={!!recordingId}
      onOpenChange={(_, state) => !state.open && onClose()}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <div className={styles.headerActions}>
              <Tooltip content="Ouvrir dans une nouvelle fenêtre" relationship="label">
                <Button
                  appearance="subtle"
                  aria-label="Ouvrir dans une nouvelle fenêtre"
                  icon={<WindowNew24Regular />}
                  onClick={handleOpenNewWindow}
                />
              </Tooltip>
              <Button
                appearance="subtle"
                aria-label="Fermer"
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            </div>
          }
        >
          {isLoading || !data ? '' : data.name}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {isLoading ? (
          <Spinner label="Loading..." size="large" />
        ) : recordingId && data ? (
          <div className={styles.wrapperStyle}>
            <GlobalInformations
              data={data}
              onRename={(name) => {
                if (recordingId) {
                  mutate({
                    recordingId,
                    name,
                  });
                }
              }}
              ref={globalInfosRef}
            />
            {data.report && (
              <>
                <TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
                  <Tab
                    id="meeting-notes"
                    icon={<TextBulletListSquareSparkleRegular />}
                    value="meeting-notes"
                  >
                    Compte-rendu
                  </Tab>
                  <Tab
                    id="meeting-transcript"
                    icon={<DocumentOnePageSparkleRegular />}
                    value="meeting-transcript"
                  >
                    Transcription
                  </Tab>
                </TabList>
                <div>
                  {selectedTab === 'meeting-notes' && (
                    <MeetingNotes
                      recordingId={recordingId}
                      report={data.report}
                      globalInfosRef={globalInfosRef}
                      editable={editable}
                      userContext={data.userContext}
                      transcriptExpired={data.transcriptExpired}
                    />
                  )}
                  {selectedTab === 'meeting-transcript' && (
                    <MeetingTranscript
                      recordingId={recordingId}
                      transcript={data.transcript}
                      editable={editable}
                      transcriptExpired={data.transcriptExpired}
                      transcriptExpiredAt={data.transcriptExpiredAt}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <></>
        )}
      </DrawerBody>
    </OverlayDrawer>
  );
};
